// components/expenses/ExpenseAuditDialog.jsx
import React, { useState, useEffect, useCallback } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    Chip,
    CircularProgress,
    Timeline,
    TimelineItem,
    TimelineSeparator,
    TimelineConnector,
    TimelineContent,
    TimelineDot,
    TimelineOppositeContent,
    Paper,
    Stack,
    Divider,
    Tooltip,
    TablePagination,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
} from '@mui/material';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    RestoreFromTrash as RestoreIcon,
    DeleteForever as PermanentDeleteIcon,
    CheckCircle as ApproveIcon,
    Cancel as RejectIcon,
    Payment as PaidIcon,
    History as HistoryIcon,
    ArrowForward as ArrowIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { toast } from 'react-toastify';
import { expenseAPI } from '../../services/api';

// ── Action config ─────────────────────────────────────────────────────────
const ACTION_CONFIG = {
    created: {
        icon: <AddIcon />,
        color: 'success',
        dotColor: 'success',
    },
    updated: {
        icon: <EditIcon />,
        color: 'primary',
        dotColor: 'primary',
    },
    deleted: {
        icon: <DeleteIcon />,
        color: 'error',
        dotColor: 'error',
    },
    restored: {
        icon: <RestoreIcon />,
        color: 'success',
        dotColor: 'success',
    },
    permanent_deleted: {
        icon: <PermanentDeleteIcon />,
        color: 'error',
        dotColor: 'error',
    },
    approved: {
        icon: <ApproveIcon />,
        color: 'success',
        dotColor: 'success',
    },
    rejected: {
        icon: <RejectIcon />,
        color: 'warning',
        dotColor: 'warning',
    },
    marked_as_paid: {
        icon: <PaidIcon />,
        color: 'info',
        dotColor: 'info',
    },
};

const ALL_ACTIONS = [
    'created', 'updated', 'deleted', 'restored',
    'permanent_deleted', 'approved', 'rejected', 'marked_as_paid',
];

const ExpenseAuditDialog = ({ open, onClose, expense }) => {
    const { t } = useTranslation();
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(0);
    const [limit, setLimit] = useState(25);
    const [total, setTotal] = useState(0);
    const [actionFilter, setActionFilter] = useState('');

    const fetchAuditLogs = useCallback(async () => {
        if (!expense?._id) return;
        setLoading(true);
        try {
            const params = {
                page: page + 1,
                limit,
            };
            if (actionFilter) params.action = actionFilter;

            const res = await expenseAPI.getAuditTrail(expense._id, params);
            setLogs(res.data?.data || []);
            setTotal(res.data?.pagination?.total || 0);
        } catch (err) {
            toast.error(
                err.response?.data?.message ||
                t('expenses.audit.fetchError', 'Failed to fetch audit trail')
            );
        } finally {
            setLoading(false);
        }
    }, [expense?._id, page, limit, actionFilter, t]);

    useEffect(() => {
        if (open && expense?._id) {
            fetchAuditLogs();
        }
    }, [open, fetchAuditLogs]);

    useEffect(() => {
        if (!open) {
            setLogs([]);
            setPage(0);
            setActionFilter('');
        }
    }, [open]);

    const formatDateTime = (date) => {
        if (!date) return '-';
        return format(new Date(date), 'dd/MM/yyyy HH:mm:ss');
    };

    const getActionLabel = (action) => {
        const labels = {
            created: t('expenses.audit.created', 'Created'),
            updated: t('expenses.audit.updated', 'Updated'),
            deleted: t('expenses.audit.deleted', 'Deleted'),
            restored: t('expenses.audit.restored', 'Restored'),
            permanent_deleted: t('expenses.audit.permanentDeleted', 'Permanently Deleted'),
            approved: t('expenses.audit.approved', 'Approved'),
            rejected: t('expenses.audit.rejected', 'Rejected'),
            marked_as_paid: t('expenses.audit.markedAsPaid', 'Marked as Paid'),
        };
        return labels[action] || action;
    };

    const getFieldLabel = (field) => {
        const labels = {
            name: t('expenses.description', 'Description'),
            amount: t('expenses.amount', 'Amount'),
            category: t('common.category', 'Category'),
            branch: t('common.branch', 'Branch'),
            date: t('expenses.date', 'Date'),
            notes: t('expenses.notes', 'Notes'),
            status: t('expenses.status', 'Status'),
        };
        return labels[field] || field;
    };

    const formatFieldValue = (field, value) => {
        if (value === null || value === undefined) return '-';
        if (field === 'date') {
            return format(new Date(value), 'dd/MM/yyyy');
        }
        if (field === 'amount') {
            return Number(value).toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
            });
        }
        return String(value);
    };

    const renderChanges = (log) => {
        if (!log.changes || log.changes.length === 0) return null;

        return (
            <Box sx={{ mt: 1 }}>
                <Typography variant="caption" fontWeight="bold" color="text.secondary">
                    {t('expenses.audit.changes', 'Changes')}:
                </Typography>
                {log.changes.map((change, idx) => (
                    <Box
                        key={idx}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            mt: 0.5,
                            pl: 1,
                        }}
                    >
                        <Chip
                            label={getFieldLabel(change.field)}
                            size="small"
                            variant="outlined"
                            sx={{ minWidth: 80 }}
                        />
                        <Typography
                            variant="caption"
                            sx={{
                                textDecoration: 'line-through',
                                color: 'error.main',
                                maxWidth: 150,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                            }}
                        >
                            {formatFieldValue(change.field, change.oldValue)}
                        </Typography>
                        <ArrowIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                        <Typography
                            variant="caption"
                            sx={{
                                color: 'success.main',
                                fontWeight: 'bold',
                                maxWidth: 150,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                            }}
                        >
                            {formatFieldValue(change.field, change.newValue)}
                        </Typography>
                    </Box>
                ))}
            </Box>
        );
    };

    const renderStatusTransition = (log) => {
        const { statusBefore, statusAfter } = log.metadata || {};
        if (!statusBefore && !statusAfter) return null;

        return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                {statusBefore && (
                    <Chip label={statusBefore} size="small" variant="outlined" />
                )}
                {statusBefore && statusAfter && (
                    <ArrowIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                )}
                {statusAfter && (
                    <Chip
                        label={statusAfter}
                        size="small"
                        color={
                            statusAfter === 'approved' ? 'success' :
                                statusAfter === 'rejected' ? 'error' :
                                    statusAfter === 'paid' ? 'info' :
                                        statusAfter === 'deleted' ? 'error' :
                                            'warning'
                        }
                    />
                )}
            </Box>
        );
    };

    if (!expense) return null;

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <HistoryIcon color="primary" />
                {t('expenses.audit.title', 'Audit Trail')}
                <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                    — {expense.name}
                </Typography>
            </DialogTitle>

            <DialogContent>
                {/* Filter */}
                <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                    <FormControl size="small" sx={{ minWidth: 200 }}>
                        <InputLabel>{t('expenses.audit.filterByAction', 'Filter by Action')}</InputLabel>
                        <Select
                            value={actionFilter}
                            onChange={(e) => {
                                setActionFilter(e.target.value);
                                setPage(0);
                            }}
                            label={t('expenses.audit.filterByAction', 'Filter by Action')}
                        >
                            <MenuItem value="">
                                {t('common.all', 'All')}
                            </MenuItem>
                            {ALL_ACTIONS.map((action) => (
                                <MenuItem key={action} value={action}>
                                    {getActionLabel(action)}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <Chip
                        label={`${total} ${t('expenses.audit.entries', 'entries')}`}
                        size="small"
                        variant="outlined"
                    />
                </Box>

                <Divider sx={{ mb: 2 }} />

                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                        <CircularProgress />
                    </Box>
                ) : logs.length === 0 ? (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                        <HistoryIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                        <Typography color="text.secondary">
                            {t('expenses.audit.noLogs', 'No audit logs found')}
                        </Typography>
                    </Box>
                ) : (
                    <Box sx={{ maxHeight: 500, overflow: 'auto' }}>
                        {logs.map((log, index) => {
                            const config = ACTION_CONFIG[log.action] || {
                                icon: <HistoryIcon />,
                                color: 'default',
                                dotColor: 'grey',
                            };

                            return (
                                <Paper
                                    key={log._id}
                                    variant="outlined"
                                    sx={{
                                        p: 2,
                                        mb: 1.5,
                                        borderLeft: 4,
                                        borderLeftColor: `${config.color}.main`
                                    }}
                                >
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Tooltip title={getActionLabel(log.action)}>
                                                <Box sx={{ color: `${config.color}.main`, display: 'flex' }}>
                                                    {config.icon}
                                                </Box>
                                            </Tooltip>
                                            <Box>
                                                <Chip
                                                    label={getActionLabel(log.action)}
                                                    size="small"
                                                    color={config.color}
                                                    variant="filled"
                                                />
                                                <Typography variant="body2" sx={{ mt: 0.5 }}>
                                                    {t('expenses.audit.by', 'by')}{' '}
                                                    <strong>
                                                        {log.performedByUser?.name || log.performedByUser?.username || '-'}
                                                    </strong>
                                                </Typography>
                                            </Box>
                                        </Box>
                                        <Typography variant="caption" color="text.secondary">
                                            {formatDateTime(log.createdAt)}
                                        </Typography>
                                    </Box>

                                    {/* Status Transition */}
                                    {renderStatusTransition(log)}

                                    {/* Field-level Changes */}
                                    {renderChanges(log)}

                                    {/* Deletion Reason */}
                                    {log.metadata?.reason && (
                                        <Box sx={{ mt: 1, pl: 1 }}>
                                            <Typography variant="caption" color="text.secondary">
                                                {t('expenses.audit.reason', 'Reason')}:{' '}
                                            </Typography>
                                            <Typography variant="caption" fontStyle="italic">
                                                {log.metadata.reason}
                                            </Typography>
                                        </Box>
                                    )}

                                    {/* Snapshot Summary */}
                                    {log.snapshot && (
                                        <Box
                                            sx={{
                                                mt: 1,
                                                p: 1,
                                                backgroundColor: 'action.hover',
                                                borderRadius: 1,
                                                display: 'flex',
                                                gap: 2,
                                                flexWrap: 'wrap',
                                            }}
                                        >
                                            <Typography variant="caption">
                                                <strong>{t('expenses.amount', 'Amount')}:</strong>{' '}
                                                {log.snapshot.amount?.toLocaleString(undefined, {
                                                    minimumFractionDigits: 2,
                                                    maximumFractionDigits: 2,
                                                })}
                                            </Typography>
                                            {log.snapshot.categoryName && (
                                                <Typography variant="caption">
                                                    <strong>{t('common.category', 'Category')}:</strong>{' '}
                                                    {log.snapshot.categoryName}
                                                </Typography>
                                            )}
                                            {log.snapshot.branchName && (
                                                <Typography variant="caption">
                                                    <strong>{t('common.branch', 'Branch')}:</strong>{' '}
                                                    {log.snapshot.branchName}
                                                </Typography>
                                            )}
                                        </Box>
                                    )}
                                </Paper>
                            );
                        })}
                    </Box>
                )}

                {/* Pagination */}
                {total > 0 && (
                    <TablePagination
                        component="div"
                        count={total}
                        page={page}
                        onPageChange={(_, newPage) => setPage(newPage)}
                        rowsPerPage={limit}
                        onRowsPerPageChange={(e) => {
                            setLimit(parseInt(e.target.value, 10));
                            setPage(0);
                        }}
                        rowsPerPageOptions={[10, 25, 50]}
                        labelRowsPerPage={t('common.rowsPerPage', 'Rows per page')}
                    />
                )}
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={onClose}>
                    {t('common.close', 'Close')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ExpenseAuditDialog;